import React, { useEffect, useRef } from "react";
import { Conversation } from "@/src/types/Entities/Messaging/Conversation";
import { Box } from "@mui/material";
import MessageCenterMessage from "./MessageCenterMessage";
import { markMessagesAsRead } from "@/src/services/messagingService";
import { queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { useQueryClient } from "@tanstack/react-query";
import { Message } from "@/src/types/Entities/Messaging/Message";
import { MinimalCase } from "@/src/types/Entities/MinimalCase";
import Typography from "@mui/material/Typography";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";
import BackLink from "./BackLink";

type ConversationMessagesProps = {
  conversation: Conversation;
  product: MinimalCase;
};

export default function ConversationMessages({ conversation, product }: ConversationMessagesProps) {
  const queryClient = useQueryClient();
  const conversationRef = useRef<null | HTMLDivElement>(null!);

  const sortComparisonFunction = (MessageA: Message, MessageB: Message) => {
    if (MessageA.created < MessageB.created) {
      return -1;
    }
    if (MessageA.created > MessageB.created) {
      return 1;
    }
    return 0;
  };

  const sortedMessages = conversation.containedMessages.toSorted(sortComparisonFunction);

  useEffect(() => {
    markMessagesAsRead(conversation.containedMessages).then(() => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.collection("/conversations"),
      });
    });

    if (conversation.containedMessages.length > 1) {
      conversationRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <>
      <Box ref={conversationRef} />
      <BackLink />
      <ContentBox headline={conversation.subject} showDivider={true}>
        <Box>
          {sortedMessages.map((message, index) => (
            <React.Fragment key={message.id}>
              {index === sortedMessages.length - 1 && <Box ref={conversationRef} />}
              <MessageCenterMessage message={message} product={product} />
            </React.Fragment>
          ))}
          {conversation.isClosed && (
            <Typography sx={{ textAlign: "center", margin: "2rem 0" }}>
              <strong>Diese Unterhaltung wurde beendet. Sie können nicht mehr darauf antworten.</strong>
            </Typography>
          )}
        </Box>
      </ContentBox>
    </>
  );
}
